import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  HStack,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { TopDonor } from "../../../api/clientUser/getClientAnalytics";

interface TopDonorsTableProps {
  donors: TopDonor[];
}

export const TopDonorsTable = ({ donors }: TopDonorsTableProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  return (
    <Box
      bg="white"
      p={{ base: 4, md: 8 }}
      borderRadius="24px"
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px solid"
      borderColor="gray.100"
    >
      <Heading size={{ base: "sm", md: "md" }} color="#1A202C" fontWeight="700" mb={4}>
        Top Donors
      </Heading>

      {donors.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={8}>
          No donors yet. Start your campaign to see results!
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th
                  color="gray.600"
                  fontWeight="700"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Rank
                </Th>
                <Th
                  color="gray.600"
                  fontWeight="700"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Donor
                </Th>
                <Th
                  color="gray.600"
                  fontWeight="700"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Amount
                </Th>
                <Th
                  color="gray.600"
                  fontWeight="700"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  display={{ base: "none", md: "table-cell" }}
                >
                  Type
                </Th>
                <Th
                  color="gray.600"
                  fontWeight="700"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Contact
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {donors.map((donor, index) => (
                <Tr key={index} _hover={{ bg: "gray.50" }} transition="all 0.2s">
                  <Td>
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      width="28px"
                      height="28px"
                      borderRadius="full"
                      bg={index < 3 ? "#339CFF15" : "gray.100"}
                      fontWeight="700"
                      fontSize="sm"
                      color={index < 3 ? "#339CFF" : "gray.600"}
                    >
                      {index + 1}
                    </Box>
                  </Td>
                  <Td>
                    <Text fontWeight="600" color="#1A202C">
                      {donor.name}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontWeight="700" color="#00C851" fontSize="md">
                      {formatCurrency(donor.amount)}
                    </Text>
                  </Td>
                  <Td display={{ base: "none", md: "table-cell" }}>
                    <Badge
                      colorScheme={donor.type === "Recurring" ? "blue" : "purple"}
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {donor.type}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      {donor.email && (
                        <Tooltip label={donor.email} placement="top">
                          <IconButton
                            as="a"
                            href={`mailto:${donor.email}`}
                            aria-label="Email donor"
                            icon={<EmailIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                          />
                        </Tooltip>
                      )}
                      {donor.phone && (
                        <Tooltip label={donor.phone} placement="top">
                          <IconButton
                            as="a"
                            href={`tel:${donor.phone}`}
                            aria-label="Call donor"
                            icon={<PhoneIcon />}
                            size="sm"
                            colorScheme="green"
                            variant="ghost"
                          />
                        </Tooltip>
                      )}
                      {!donor.email && !donor.phone && (
                        <Text fontSize="sm" color="gray.400">
                          —
                        </Text>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
